import React, { useState, useEffect, useRef } from 'react';

const AnimatedCounter = ({ targetValue, duration = 2000, className = "" }) => {
  const hasAnimatedRef = useRef(false);
  const elementRef = useRef(null);
  const animationRef = useRef(null);
  const [count, setCount] = useState(0);

  // Parse the target value and extract number and suffix
  const parseTargetValue = (value) => {
    const match = value.match(/^(\d{1,3}(?:,\d{3})*)(.*)/);
    if (match) {
      const numericValue = parseInt(match[1].replace(/,/g, ''));
      const suffix = match[2];
      return { number: numericValue, suffix, hasComma: match[1].includes(',') };
    }
    return { number: 0, suffix: value, hasComma: false };
  };

  const { number: targetNumber, suffix, hasComma } = parseTargetValue(targetValue);

  // Format number with commas if original had commas
  const formatNumber = (num) => {
    if (hasComma && num >= 1000) {
      return num.toLocaleString();
    }
    return num.toString();
  };

  // Easing function for smooth animation
  const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

  const animateCount = () => {
    if (hasAnimatedRef.current) return;
    
    hasAnimatedRef.current = true;
    const startTime = Date.now();
    const startValue = 0;

    const animate = () => {
      const currentTime = Date.now();
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      const easedProgress = easeOutQuart(progress);
      const currentValue = Math.floor(startValue + (targetNumber - startValue) * easedProgress);
      
      setCount(currentValue);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        // Ensure final value is set exactly
        setCount(targetNumber);
      }
    };

    animationRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    // If already animated, set to final value immediately
    if (hasAnimatedRef.current) {
      setCount(targetNumber);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimatedRef.current) {
            // Add a small delay to make the animation more noticeable
            setTimeout(() => animateCount(), 200);
          }
        });
      },
      {
        threshold: 0.5, // Trigger when 50% of the element is visible
        rootMargin: '0px'
      }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      if (elementRef.current) {
        observer.unobserve(elementRef.current);
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [targetNumber]);

  return (
    <span ref={elementRef} className={className}>
      {formatNumber(count)}{suffix}
    </span>
  );
};

export default AnimatedCounter;